import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FaFileDownload } from "react-icons/fa";
import api, { BACKEND_URL } from '../api/axios';

const COLORS = { blue: "#2563EB", orange: "#F59E0B", dark: "#0D1B3D" };

export default function OfferRespond() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [signatureName, setSignatureName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api
      .get(`/offers/respond/${token}`)
      .then((res) => setOffer(res.data))
      .catch(() => setError("This offer link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const respond = async (decision) => {
    if (decision === "accepted" && !signatureName.trim()) {
      setError("Please type your full name to accept.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/offers/respond/${token}`, {
        decision,
        signature_name: decision === "accepted" ? signatureName.trim() : undefined,
      });
      setDone(decision);
    } catch (err) {
      setError(err?.response?.data?.message || "Couldn't submit your response.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="py-24 text-center text-gray-400">Loading offer...</div>;
  }

  if (error && !offer) {
    return <div className="py-24 text-center text-red-500">{error}</div>;
  }

  if (done) {
    const messages = {
      accepted: "You've accepted this offer. The hiring team has been notified.",
      rejected: "You've declined this offer.",
      negotiation: "Your request to discuss this offer has been sent to the hiring team.",
    };
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <p className="text-lg font-medium" style={{ color: COLORS.dark }}>
          {messages[done]}
        </p>
      </div>
    );
  }

  const formatSalary = (v) => "₹" + Number(v).toLocaleString("en-IN");
  const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : "—";

  return (
    <div className="mx-auto max-w-lg px-4 py-12">
      <div className="rounded-xl border border-gray-200 shadow-sm">
        <div className="rounded-t-xl px-6 py-5" style={{ backgroundColor: COLORS.dark }}>
          <h1 className="text-lg font-semibold text-white">Offer for {offer.candidate_name}</h1>
        </div>

        <div className="space-y-4 px-6 py-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs uppercase text-gray-400">Salary</div>
              <div className="text-lg font-semibold" style={{ color: COLORS.dark }}>
                {formatSalary(offer.salary)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-400">Joining Date</div>
              <div className="text-lg font-semibold" style={{ color: COLORS.dark }}>
                {formatDate(offer.joining_date)}
              </div>
            </div>
            {offer.reporting_manager && (
              <div>
                <div className="text-xs uppercase text-gray-400">Reporting Manager</div>
                <div className="font-medium" style={{ color: COLORS.dark }}>
                  {offer.reporting_manager}
                </div>
              </div>
            )}
            {offer.location && (
              <div>
                <div className="text-xs uppercase text-gray-400">Location</div>
                <div className="font-medium" style={{ color: COLORS.dark }}>
                  {offer.location}
                </div>
              </div>
            )}
          </div>

          {offer.pdf_path && (
            <a
               href={`${api.defaults.baseURL}/offers/respond/${token}/pdf`}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-2 text-sm font-medium"
    style={{ color: COLORS.blue }}
            >
              <FaFileDownload /> View full offer letter
            </a>
          )}

          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
          )}

          <div className="border-t pt-4">
            <label className="mb-1 block text-sm font-medium" style={{ color: COLORS.dark }}>
              Type your full name to sign and accept
            </label>
            <input
              type="text"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              className="mb-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
              placeholder="Full name"
            />
            <div className="flex flex-wrap gap-2">
              <button
                disabled={submitting}
                onClick={() => respond("accepted")}
                className="flex-1 rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                style={{ backgroundColor: "#16A34A" }}
              >
                Accept Offer
              </button>
              <button
                disabled={submitting}
                onClick={() => respond("negotiation")}
                className="flex-1 rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                style={{ backgroundColor: COLORS.orange }}
              >
                Request Discussion
              </button>
              <button
                disabled={submitting}
                onClick={() => respond("rejected")}
                className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 disabled:opacity-60"
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}